import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatErr, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { EventCard } from "@/pages/Events";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const EventForm = ({ initial, onSubmit, submitLabel, editing = false }) => {
  const [f, setF] = useState({ kind: "event", ...(initial || { title: "", description: "", location: "", date: "", price: 25000 }) });
  const [applyAll, setApplyAll] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, price: Number(f.price), apply_price_to_all: applyAll }); }}>
      <div className="space-y-2">
        <Label>Jenis album</Label>
        <div className="grid grid-cols-2 gap-2" data-testid="event-form-kind">
          {[["event", "Event", "Lomba, konser, wisuda"], ["non_event", "Non Event", "Foto harian, wisata, CFD"]].map(([k, l, hint]) => (
            <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} data-testid={`event-form-kind-${k}`}
              className={`rounded-xl border px-3 py-2 text-left transition-colors ${f.kind === k ? "border-blue-600 bg-blue-50 ring-2 ring-blue-500/20" : "border-zinc-200 hover:border-zinc-400"}`}>
              <p className="text-sm font-semibold">{l}</p><p className="text-xs text-zinc-500">{hint}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2"><Label>{f.kind === "non_event" ? "Nama album" : "Nama event"}</Label><Input required value={f.title} onChange={set("title")} data-testid="event-form-title-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Tanggal</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="event-form-date-input" /></div>
        <div className="space-y-2"><Label>Harga jual per foto (Rp)</Label><Input type="number" min={0} required value={f.price} onChange={set("price")} data-testid="event-form-price-input" /></div>
      </div>
      {editing ? (
        <label className="flex items-start gap-2 text-sm text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} className="mt-1 accent-blue-600" data-testid="event-form-apply-all-checkbox" />
          Terapkan harga ini ke semua foto yang sudah diunggah (harga khusus per foto akan ditimpa)
        </label>
      ) : (
        <p className="text-xs text-zinc-500 -mt-2">Harga ini otomatis dipakai untuk setiap foto yang diunggah. Harga per foto & paket bisa diatur nanti.</p>
      )}
      <div className="space-y-2"><Label>Lokasi</Label><Input value={f.location} onChange={set("location")} data-testid="event-form-location-input" /></div>
      <div className="space-y-2"><Label>Deskripsi</Label><Textarea value={f.description} onChange={set("description")} data-testid="event-form-description-input" /></div>
      <Button className="w-full rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="event-form-submit-button">{submitLabel}</Button>
    </form>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-5">
    <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-2" data-testid={testId}>{value}</p>
  </div>
);

const QueueBanner = () => {
  const [q, setQ] = useState(null);
  useEffect(() => {
    let timer;
    const loop = () => api.get("/photographer/queue").then((r) => {
      setQ(r.data);
      if (r.data.queued + r.data.working > 0) timer = setTimeout(loop, 6000);
    }).catch(() => {});
    loop();
    return () => clearTimeout(timer);
  }, []);
  if (!q || !q.events.length) return null;
  return (
    <div className="mb-8 rounded-2xl bg-blue-50 border border-blue-200 p-4 text-sm" data-testid="queue-banner">
      <p className="font-semibold text-blue-900 mb-2">{q.queued + q.working} foto sedang diproses server{q.failed ? ` · ${q.failed} gagal` : ""}</p>
      <div className="flex flex-wrap gap-2">
        {q.events.map((e) => (
          <Link key={e.event_id} to={`/fotografer/event/${e.event_id}`} className="rounded-full bg-white border border-blue-200 px-3 py-1 text-blue-800 hover:border-blue-400" data-testid={`queue-banner-event-${e.event_id}`}>
            {e.event_title}: {e.queued + e.working} antre{e.failed ? `, ${e.failed} gagal` : ""}
          </Link>
        ))}
      </div>
    </div>
  );
};

const PayoutHistory = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/photographer/payouts").then((r) => setList(r.data)); }, []);
  return (
    <section className="mt-14">
      <h2 className="text-lg font-bold mb-5">Riwayat pencairan dana</h2>
      <div className="rounded-2xl bg-white border border-zinc-200 divide-y divide-zinc-100" data-testid="pg-payout-history">
        {list.map((p) => (
          <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`pg-payout-${p.id}`}>
            <div>
              <p className="font-semibold">{new Date(p.created_at).toLocaleDateString("id-ID", { dateStyle: "long" })}</p>
              <p className="text-xs text-zinc-500">{p.order_codes.length} pesanan · {p.bank_name} {p.bank_account} · Ref: {p.reference || "-"}</p>
            </div>
            <span className="font-mono-x font-semibold text-emerald-700">{rupiah(p.amount)}</span>
          </div>
        ))}
        {list.length === 0 && <p className="p-6 text-sm text-zinc-500">Belum ada pencairan.</p>}
      </div>
    </section>
  );
};

export default function PhotographerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [kindTab, setKindTab] = useState("all");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/photographer/stats").then((r) => setStats(r.data));
    api.get("/events", { params: { photographer_id: user.id } }).then((r) => setEvents(r.data));
  }, [user.id]);

  const create = async (body) => {
    try {
      const { data } = await api.post("/events", body);
      toast.success("Event dibuat. Sekarang unggah fotonya.");
      nav(`/fotografer/event/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Dashboard Fotografer" title={user.name}>
        <div className="flex gap-2">
        <Button asChild variant="outline" className="rounded-xl h-11 bg-white" data-testid="view-public-profile-button"><Link to={`/studio/${user.id}`}>Profil publik</Link></Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11" data-testid="create-event-button"><Plus className="mr-2 h-4 w-4" />Album baru</Button></DialogTrigger>
          <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Buat album baru</DialogTitle></DialogHeader><EventForm onSubmit={create} submitLabel="Buat album" /></DialogContent>
        </Dialog>
        </div>
      </PageHeader>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat label={`Pendapatan bersih`} value={rupiah(stats.net)} testId="stat-net" />
          <Stat label={`Komisi platform (${stats.commission_rate}%)`} value={rupiah(stats.commission)} testId="stat-commission" />
          <Stat label="Belum dicairkan" value={rupiah(stats.unpaid)} testId="stat-unpaid" />
          <Stat label="Sudah dicairkan" value={rupiah(stats.paid_out)} testId="stat-paid-out" />
          <Stat label="Foto terjual" value={stats.sold} testId="stat-sold" />
          <Stat label="Foto diunggah" value={stats.photos} testId="stat-photos" />
          <Stat label="Wajah terdeteksi" value={stats.faces} testId="stat-faces" />
          <Stat label="Perlu konfirmasi" value={stats.pending} testId="stat-pending" />
        </div>
      )}
      {!user.bank_account && <p className="mb-8 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3" data-testid="bank-warning">Isi data rekening di menu Rekening agar member bisa membayar.</p>}
      <QueueBanner />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-bold">Album saya</h2>
        <Tabs value={kindTab} onValueChange={setKindTab}>
          <TabsList data-testid="dashboard-kind-tabs">
            {[["all", "Semua"], ["event", "Event"], ["non_event", "Non Event"]].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`dashboard-kind-tab-${k}`}>{l} ({k === "all" ? events.length : events.filter((e) => e.kind === k).length})</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="photographer-events-grid">
        {events.filter((e) => kindTab === "all" || e.kind === kindTab).map((e) => <EventCard key={e.id} event={e} to={`/fotografer/event/${e.id}`} />)}
        {events.length === 0 && <p className="text-zinc-500">Belum ada event. Buat event pertamamu.</p>}
      </div>
      <PayoutHistory />
    </main>
  );
}
